import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import Flex from "../../../../../components/Flex";
import GlobalFilter from "../../../../../components/GlobalFilter";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import Text from "../../../../../components/Text";
import useActiveComponentCountController from "../../../../../components/filter/use-ActiveComponentCountController";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useProviderListV2 } from "../../../../../queries/use-provider-list-v2";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../utils/utils";

export type EraEobFilterData = {
  date_range?: string;
  startDate?: string;
  endDate?: string;
  providers?: { value: string; label: string }[];
  claim_type?: { value: string; label: string }[];
  view_claim?: string;
};

const DEFAULT_ERA_EOB_FILTERS: EraEobFilterData = {
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
  providers: undefined,
  claim_type: undefined,
  view_claim: undefined,
};

// const CLAIM_TYPE_OPTIONS: Option[] = [
//   { value: "PAPER", label: "Paper" },
//   { value: "ELECTRONIC", label: "Electronic" },
// ];

interface EraEobFiltersProps {
  onApply: (v: any) => void;
  filters: EraEobFilterData;
  setFilters: (v: EraEobFilterData) => void;
}

const EraEobFilters = ({
  onApply,
  filters,
  setFilters,
}: EraEobFiltersProps) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: filters,
    onSubmit: (v) => {
      setFilters(v as EraEobFilterData);
      onApply(v as EraEobFilterData);
    },
  });

  const [providerSearchKey, setProviderSearchKey] = useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: isFetchingProviders } =
    useProviderListV2({
      pagination: providerPagination,
      searchString: providerSearchKey,
      xTENANTID: getXtenantId(),
      status: true,
      archive: false,
    });

  const providerOptions = useMemo(() => {
    const fetched =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })) ?? [];
    const selected = (formik.values.providers ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [providersList, formik.values.providers]);

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }[]>(
    Object.keys(formik.values).map((it) => ({ [it]: false }))
  );

  function handleExpandClick(k: string, value: boolean) {
    setExpanded((prev) =>
      prev?.map((it) => {
        const curKey = Object.keys(it)[0];
        return Object.keys(it).some((obj) => obj === k)
          ? { [k]: value }
          : { [curKey]: false };
      })
    );
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeFilter = () => setAnchorEl(null);

  const activeFilterCount = useActiveComponentCountController(formik.values);

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        width={"18.438rem"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        <Box>
          <Flex width={"100%"} justifyContent={"end"}>
            <Button
              variant="text"
              onClick={() => {
                formik.resetForm();
                formik.submitForm();
                setFilters(DEFAULT_ERA_EOB_FILTERS);
              }}
            >
              Clear Filter
            </Button>
          </Flex>

          <Accordion
            variant="outlined"
            expanded={expanded[0]?.["providers"]}
            onChange={(_, newExpanded) =>
              handleExpandClick("providers", newExpanded)
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Text>Rendering Provider</Text>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 1 }}>
              <MaskMultiSelectScroll
                onSearch={(val) => setProviderSearchKey(val)}
                optionWithCheckbox={true}
                value={formik.values.providers}
                placeholder="Providers"
                options={providerOptions}
                pagination={providerPagination}
                setPagination={setProviderPagination}
                totalElements={providersList?.data?.page?.totalElements}
                loading={isFetchingProviders}
                onChange={(val) => {
                  formik.setFieldValue("providers", val);
                }}
              />
            </AccordionDetails>
          </Accordion>
          {/* {Object.keys(formik.values).map((key, ind) => {
            // return key === "date_range" ? (
            //   <Accordion
            //     key={key}
            //     variant="outlined"
            //     expanded={expanded[ind][key]}
            //     onChange={(_, newExpanded) =>
            //       handleExpandClick(key, newExpanded)
            //     }
            //   >
            //     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            //       <Text>Date (Date Range)</Text>
            //     </AccordionSummary>
            //     <AccordionDetails sx={{ padding: 1 }}>
            //       <CustomSelect
            //         value={formik.values.date_range ?? null}
            //         options={NEW_DATE_RANGE_FOR_CLAIMS_TYPE}
            //         onChange={(v) => formik.setFieldValue("date_range", v)}
            //       />
            //       {formik.values.date_range === "CUSTOM_RANGE" && (
            //         <Box>
            //           <Grid>
            //             <Grid item sx={{ my: 2 }}>
            //               <CustomDatePicker
            //                 label="Start Date"
            //                 value={formik.values.startDate}
            //                 onChange={(val) => {
            //                   formik.setFieldValue(`startDate`, val);
            //                 }}
            //               />
            //             </Grid>
            //             <Grid item sx={{ my: 2 }}>
            //               <CustomDatePicker
            //                 label="End Date"
            //                 value={formik.values.endDate}
            //                 onChange={(val) => {
            //                   formik.setFieldValue(`endDate`, val);
            //                 }}
            //               />
            //             </Grid>
            //           </Grid>
            //         </Box>
            //       )}
            //     </AccordionDetails>
            //   </Accordion>
            // ) : key === "providers" ? (
            return <Accordion
              key={key}
              variant="outlined"
              expanded={expanded[ind][key]}
              onChange={(_, newExpanded) =>
                handleExpandClick(key, newExpanded)
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Text>Rendering Provider</Text>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 1 }}>
                <MaskMultiSelect
                  onSearch={(val) => setProviderSearchKey(val)}
                  optionWithCheckbox={true}
                  value={formik.values.providers}
                  placeholder="Providers"
                  options={providersList?.data?.content?.map((it) => ({
                    value: it?.uuid,
                    label: it?.firstName + " " + it?.lastName,
                  }))}
                  onChange={(val) => {
                    formik.setFieldValue("providers", val);
                  }}
                />
              </AccordionDetails>
            </Accordion>
            // ) : key === "claim_type" ? (
            //   <Accordion
            //     key={key}
            //     variant="outlined"
            //     expanded={expanded[ind][key]}
            //     onChange={(_, newExpanded) =>
            //       handleExpandClick(key, newExpanded)
            //     }
            //   >
            //     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            //       <Text>Claim Type</Text>
            //     </AccordionSummary>
            //     <AccordionDetails sx={{ padding: 1 }}>
            //       <MaskMultiSelect
            //         optionWithCheckbox={true}
            //         value={formik.values.claim_type}
            //         placeholder="Claim Type"
            //         options={CLAIM_TYPE_OPTIONS}
            //         onChange={(val) => {
            //           formik.setFieldValue("claim_type", val);
            //         }}
            //       />
            //     </AccordionDetails>
            //   </Accordion>
            // ) : key === "view_claim" ? (
            //   <Accordion
            //     key={key}
            //     variant="outlined"
            //     expanded={expanded[ind][key]}
            //     onChange={(_, newExpanded) =>
            //       handleExpandClick(key, newExpanded)
            //     }
            //   >
            //     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            //       <Text>View Claim</Text>
            //     </AccordionSummary>
            //     <AccordionDetails sx={{ padding: 1 }}>
            //       <TextField
            //         size="small"
            //         fullWidth
            //         placeholder="Enter Claim ID"
            //         value={formik.values.view_claim ?? ""}
            //         onChange={(e) =>
            //           formik.setFieldValue("view_claim", e.target.value)
            //         }
            //       />
            //     </AccordionDetails>
            //   </Accordion>
            // ) : (
            //   <></>
            // );
          })} */}
        </Box>
        <Flex width={"100%"} justifyContent={"end"}>
          <Button
            variant="text"
            sx={{ color: "gray" }}
            onClick={() => {
              formik.resetForm();
              formik.submitForm();
              closeFilter();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => {
              formik.submitForm();
              closeFilter();
            }}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default EraEobFilters;
